import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type TranslationKey } from '@icebreaker/shared/i18n';
import { ROUTES } from '@/app/routes';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  Button,
  EmptyState,
  ErrorBox,
  SectionHeading,
  Skeleton,
  Table,
  Tag,
  useErrorMessage,
} from '@icebreaker/ui';
import {
  decideModeration,
  fetchOverview,
  fetchQueue,
  mutationReset,
  queueItemSelected,
  runSignals,
} from '../store/admin.slice';
import { ReasonDialog } from './reason-dialog';
import styles from './admin.module.css';

/**
 * Raporlar ve kötüye kullanım (tasarım ekran 27): KPI kutuları, bildirim tablosu,
 * otomatik sinyaller (KARAR VERMEZ — kuyruğa taşır) ve platform sağlığı metrikleri.
 *
 * TAŞIMA NOTU (panel 3. tur): ekran web'deki `/admin/reports` sayfasından
 * BİREBİR geldi. Tek fark yönlendiricidir — `next/navigation` yerine
 * `react-router` ve dil ÖNEKSİZ panel rotası (`ROUTES.moderation`); panelde
 * adres dil taşımaz, dil kullanıcının tercihidir (`i18n-provider`).
 *
 * SATIR EYLEMLERİ REFERANSTA İKİDİR: "İncele" (hayalet) + "Kapat" (ikincil).
 * 4. tur bölüm sayımında "Kapat" eksikti. Kapatma YENİ BİR UÇ AÇMAZ: kalemi
 * `decideModeration` ile `rejected` kararına bağlar, yani gerekçe ZORUNLUDUR
 * ve denetim kaydına yazılır (kabuk sözü: "her yıkıcı eylem gerekçe istiyor").
 */
export function ReportsScreen() {
  const t = useTranslation();
  const errorMessage = useErrorMessage();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const overview = useAppSelector((state: RootState) => state.admin.overview);
  const queue = useAppSelector((state: RootState) => state.admin.queue);
  const mutation = useAppSelector((state: RootState) => state.admin.mutation);
  const lastSignalRun = useAppSelector((state: RootState) => state.admin.lastSignalRun);
  const [dismissTarget, setDismissTarget] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    dispatch(fetchOverview.request());
    dispatch(fetchQueue.request({ status: 'all', page: 1, pageSize: 20 }));
  }, [dispatch]);

  // Kapatma başarılı → pencere kapanır (kalem slice'ta yerinde güncellendi).
  useEffect(() => {
    if (mutation.phase === 'succeeded' && dismissTarget) {
      setDismissTarget(null);
      dispatch(mutationReset());
    }
  }, [mutation.phase, dismissTarget, dispatch]);

  const kpis = overview.data?.kpis ?? null;

  return (
    <section className={styles.screen}>
      <h1 className={styles.title}>{t.t('admin.reports.title')}</h1>
      <p className={styles.lead}>{t.t('admin.reports.lead')}</p>

      <ErrorBox error={overview.error} className={styles.errorBox}>
        <Button variant="secondary" size="sm" onClick={() => dispatch(fetchOverview.request())}>
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      {kpis === null && overview.phase === 'loading' ? (
        <div style={{ marginTop: 'var(--ib-space-4)' }}>
          <Skeleton label={t.t('a11y.loadingContent')} lines={2} lineHeight={64} />
        </div>
      ) : kpis !== null ? (
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>{t.t('admin.reports.kpiOpen')}</div>
            <div className={styles.kpiValue}>{t.formatNumber(kpis.openReports)}</div>
            <div className={styles.kpiNote}>
              {t.t('admin.reports.kpiOpenNote', { count: kpis.openReportsUnder24h })}
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>{t.t('admin.reports.kpiAvg')}</div>
            <div className={styles.kpiValue}>
              {kpis.avgResolutionHours === null
                ? '—'
                : t.t('admin.reports.kpiAvgValue', { hours: kpis.avgResolutionHours })}
            </div>
            <div className={styles.kpiNote}>{t.t('admin.reports.kpiAvgNote')}</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>{t.t('admin.reports.kpiSuspended')}</div>
            <div className={styles.kpiValue}>{t.formatNumber(kpis.suspendedLast30Days)}</div>
            <div className={styles.kpiNote}>{t.t('admin.reports.kpiSuspendedNote')}</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>{t.t('admin.reports.kpiAppeals')}</div>
            <div className={styles.kpiValue}>{t.formatNumber(kpis.openAppeals)}</div>
            <div className={styles.kpiNote}>{t.t('admin.reports.kpiAppealsNote')}</div>
          </div>
        </div>
      ) : null}

      <div className={styles.reportsTable}>
        {queue.phase === 'loading' && queue.items.length === 0 ? (
          <Skeleton label={t.t('a11y.loadingContent')} variant="people" lines={5} />
        ) : queue.items.length === 0 && queue.phase === 'succeeded' ? (
          <EmptyState
            title={t.t('admin.reports.emptyTableTitle')}
            body={t.t('admin.reports.emptyTableBody')}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t.t('admin.reports.table.kind')}</th>
                <th>{t.t('admin.reports.table.about')}</th>
                <th>{t.t('admin.reports.table.signal')}</th>
                <th>{t.t('admin.reports.table.source')}</th>
                <th>{t.t('admin.reports.table.status')}</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {queue.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className={styles.personName}>
                      {t.t(`admin.moderation.types.${item.itemType}` as TranslationKey)}
                    </span>
                    <span className={styles.personMail}>
                      {t.formatDate(item.createdAt, { dateStyle: 'medium' })}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--ib-font-size-sm)' }}>{item.target.label || '—'}</td>
                  <td className={styles.signalCell}>
                    {item.reason
                      ? t.t('admin.reports.signalSummary', {
                          count: item.reportCount,
                          reason: t.t(`admin.moderation.reasons.${item.reason}` as TranslationKey),
                        })
                      : t.t(`admin.moderation.types.${item.itemType}` as TranslationKey)}
                  </td>
                  <td className={styles.cellMeta}>
                    {t.t(`admin.moderation.sources.${item.source}` as TranslationKey)}
                  </td>
                  <td>
                    <Tag variant={item.priority === 'high' ? 'warning' : 'outline'}>
                      {t.t(`admin.moderation.statuses.${item.status}` as TranslationKey)}
                    </Tag>
                  </td>
                  <td>
                    <span className={styles.rowActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          dispatch(queueItemSelected(item.id));
                          // `navigate` Promise döndürür (react-router 7); gezinme beklenmez.
                          void navigate(ROUTES.moderation);
                        }}
                      >
                        {t.t('admin.reports.review')}
                      </Button>
                      {/*
                        "Kapat" burada pencere kapatmak değil, bildirimi işlemsiz
                        kapatma kararıdır (`decision: 'rejected'`): olumsuz eylem,
                        çerçeveli kırmızı; onay penceresi dolu kırmızı (19.10).
                      */}
                      {item.status === 'open' || item.status === 'info_requested' ? (
                        <Button
                          variant="dangerOutline"
                          size="sm"
                          onClick={() => {
                            dispatch(mutationReset());
                            setDismissTarget({ id: item.id, label: item.target.label || '—' });
                          }}
                        >
                          {t.t('admin.reports.dismiss')}
                        </Button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <div className={styles.twoCol}>
        <div>
          <SectionHeading first title={t.t('admin.reports.signalsTitle')} />
          {(overview.data?.signals ?? []).map((signal) => (
            <div key={signal.id} className={styles.signalRow}>
              <span className={styles.signalText}>
                <span className={styles.signalName}>
                  {t.t(`admin.reports.signals.${signal.id}.name` as TranslationKey)}
                </span>
                <span className={styles.signalDesc}>
                  {t.t(`admin.reports.signals.${signal.id}.desc` as TranslationKey)}
                </span>
              </span>
              <Tag variant="neutral">
                {t.t(`admin.reports.signalStates.${signal.state}` as TranslationKey)}
              </Tag>
            </div>
          ))}
          <div className={styles.signalRunRow}>
            <Button
              variant="secondary"
              loading={mutation.phase === 'loading'}
              loadingLabel={t.t('admin.reports.runningSignals')}
              onClick={() => dispatch(runSignals.request())}
            >
              {t.t('admin.reports.runSignals')}
            </Button>
            {lastSignalRun !== null && mutation.phase === 'succeeded' ? (
              <span className={styles.signalRunResult} role="status">
                {t.t('admin.reports.runResult', { count: lastSignalRun })}
              </span>
            ) : null}
          </div>
        </div>

        <div>
          <SectionHeading first title={t.t('admin.reports.healthTitle')} />
          {(overview.data?.health ?? []).map((metric) => (
            <div key={metric.id} className={styles.healthRow}>
              <div className={styles.healthTop}>
                <span className={styles.healthLabel}>
                  {t.t(`admin.reports.health.${metric.id}` as TranslationKey)}
                </span>
                <span className={styles.healthValue}>
                  {metric.ratio === null ? t.t('admin.reports.noData') : `%${metric.ratio}`}
                </span>
              </div>
              <div className={styles.healthBar} aria-hidden="true">
                <div className={styles.healthFill} style={{ width: `${metric.ratio ?? 0}%` }} />
              </div>
            </div>
          ))}
          <p className={styles.healthNote}>{t.t('admin.reports.healthNote')}</p>
        </div>
      </div>

      {/* KAPATMA GEREKÇESİZ OLMAZ: karar denetim kaydına gerekçesiyle yazılır. */}
      <ReasonDialog
        open={dismissTarget !== null}
        tone="negative"
        title={`${t.t('admin.reports.dismissTitle')} · ${dismissTarget?.label ?? ''}`}
        body={
          <>
            {t.t('admin.reports.dismissBody')}
            {mutation.error ? (
              <span className={styles.errorBox} role="alert" style={{ display: 'block' }}>
                {errorMessage(mutation.error)}
              </span>
            ) : null}
          </>
        }
        confirmLabel={t.t('admin.reports.dismiss')}
        loading={mutation.phase === 'loading'}
        onConfirm={(reason) => {
          if (!dismissTarget) return;
          dispatch(
            decideModeration.request({
              itemId: dismissTarget.id,
              input: { decision: 'rejected', reason },
            }),
          );
        }}
        onCancel={() => {
          setDismissTarget(null);
          dispatch(mutationReset());
        }}
      />
    </section>
  );
}
